import { useEffect, useRef } from "react";

/**
 * Modal dialog a11y helper — handles Escape-to-close, focus return on
 * close, and a simple Tab focus trap (Tab past the last focusable
 * element wraps to the first; Shift+Tab from the first wraps to the
 * last). Pass `enabled` to gate it (e.g. don't fire while busy=true).
 *
 * Pair this with `role="dialog" aria-modal="true" aria-labelledby="..."`
 * on the dialog `<div>` and id the title `<h2>`.
 *
 * Returns a `dialogRef` to attach to the dialog container so the trap
 * knows which element's descendants are in scope.
 */
export function useDialogA11y(
  open: boolean,
  onClose: () => void,
  enabled: boolean = true,
) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    // Save the trigger so we can restore focus when the modal closes.
    restoreRef.current = (document.activeElement as HTMLElement | null) ?? null;

    return () => {
      // On unmount or open=false, restore focus to whatever opened the dialog.
      const target = restoreRef.current;
      if (target && typeof target.focus === "function") {
        // Defer to the next paint to avoid the focus landing on a now-hidden
        // element (React unmount races vs effect cleanup).
        setTimeout(() => target.focus(), 0);
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open || !enabled) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open, enabled, onClose]);

  return { dialogRef };
}
